import { TimeData } from "../lib/customTypes";
import { BasicButton } from "./BasicButton";
import { DigitalClock } from "./DigitalClock";
import { ColorThemeSwitcher } from "./ColorThemeSwitcher";
import { ClockTheme } from "../lib/colorThemes";

interface ClockFaceProps {
  data: TimeData;
  eduMode: boolean;
  eduModeHandler: () => void;
  showClock: boolean;
  showClockClickHandler: () => void;
  theme: ClockTheme;
  themeChangeHandler: (themeName: string) => void;
}

const ClockFace = ({
  data,
  eduMode,
  eduModeHandler,
  showClock,
  showClockClickHandler,
  theme,
  themeChangeHandler,
}: ClockFaceProps) => {
  const { timeEls, seconds, hourRow5, hourRow1, minuteRow5, minuteRow1 } = data;

  // Foundational
  const baseFlex = "flex flex-1 justify-center items-center ";
  const baseHeight = "h-20 ";
  const baseBorderLit = "border border-stone-500 ";
  const baseBorderUnlit = "border border-stone-300 ";
  const baseShadowLit = "shadow-md ";
  const baseShadowUnlit = "shadow-md ";
  const baseText = " text-slate-600 ";
  const baseTextLight = " text-slate-100 ";

  // Colors
  const redOn = theme.hourOn;
  const redOff = theme.hourOff;
  const yellowOn = theme.minOn;
  const yellowOff = theme.minOff;

  // Rounding
  const roundingBasic = "rounded-sm mx-1 ";
  const roundingFull = "rounded-full mx-1 ";
  const roundLeft = "rounded-l-full rounded-r-none mr-1 ";
  const roundRight = "rounded-l-none rounded-r-full ml-1 ";

  // Element titles
  const titleHour5 =
    "Elements in this row each represent a 5 hour span of time";
  const titleHour1 =
    "Elements in this row each represent a 1 hour span of time";
  const titleMinute5 =
    "Elements in this row each represent a 5 minute span of time";
  const titleMinute1 =
    "Elements in this row each represent a 1 minute span of time";

  // Composing the base tiles
  const baseTileLit = baseFlex
    .concat(baseHeight)
    .concat(baseBorderLit)
    .concat(baseShadowLit);
  const baseTileUnlit = baseFlex
    .concat(baseHeight)
    .concat(baseBorderUnlit)
    .concat(baseShadowUnlit);
  const secondsTileLit = "flex justify-center items-center w-24 h-24 "
    .concat(baseBorderLit)
    .concat(roundingFull)
    .concat(baseShadowLit);
  const secondsTileUnlit = "flex justify-center items-center w-24 h-24 "
    .concat(baseBorderUnlit)
    .concat(roundingFull)
    .concat(baseShadowUnlit);

  // Seconds blinker
  const secondsOn = secondsTileLit.concat(yellowOn);
  const secondsOff = secondsTileUnlit.concat(yellowOff);

  // 5/1-Hour rows, with left, basic, and right tiles
  const hourBasicOn = baseTileLit
    .concat(redOn)
    .concat(roundingBasic)
    .concat(baseTextLight);
  const hourBasicOff = baseTileUnlit
    .concat(redOff)
    .concat(roundingBasic)
    .concat(baseText);
  const hourLeftOn = baseTileLit
    .concat(redOn)
    .concat(roundLeft)
    .concat(baseTextLight);
  const hourLeftOff = baseTileUnlit
    .concat(redOff)
    .concat(roundLeft)
    .concat(baseText);
  const hourRightOn = baseTileLit
    .concat(redOn)
    .concat(roundRight)
    .concat(baseTextLight);
  const hourRightOff = baseTileUnlit
    .concat(redOff)
    .concat(roundRight)
    .concat(baseText);

  // 1-Minute rows, with left, basic, and right tiles
  const minLeftOn = baseTileLit
    .concat(yellowOn)
    .concat(roundLeft)
    .concat(baseText);
  const minLeftOff = baseTileUnlit
    .concat(yellowOff)
    .concat(roundLeft)
    .concat(baseText);
  const minBasicOn = baseTileLit
    .concat(yellowOn)
    .concat(roundingBasic)
    .concat(baseText);
  const minBasicOff = baseTileUnlit
    .concat(yellowOff)
    .concat(roundingBasic)
    .concat(baseText);
  const minRightOn = baseTileLit
    .concat(yellowOn)
    .concat(roundRight)
    .concat(baseText);
  const minRightOff = baseTileUnlit
    .concat(yellowOff)
    .concat(roundRight)
    .concat(baseText);

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex justify-center mb-2">
        <div
          className={seconds[0] ? secondsOn : secondsOff}
          title="This lamp will illuminate when the clock seconds are an odd number"
        >
          {eduMode ? timeEls[2] : ""}
        </div>
      </div>
      <div className="flex mb-2">
        <div
          className={hourRow5[0] ? hourLeftOn : hourLeftOff}
          title={eduMode ? titleHour5 : ""}
        >
          {eduMode ? "5" : ""}
        </div>
        <div
          className={hourRow5[1] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleHour5 : ""}
        >
          {eduMode ? "10" : ""}
        </div>
        <div
          className={hourRow5[2] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleHour5 : ""}
        >
          {eduMode ? "15" : ""}
        </div>
        <div
          className={hourRow5[3] ? hourRightOn : hourRightOff}
          title={eduMode ? titleHour5 : ""}
        >
          {eduMode ? "20" : ""}
        </div>
      </div>
      <div className="flex mb-2">
        <div
          className={hourRow1[0] ? hourLeftOn : hourLeftOff}
          title={eduMode ? titleHour1 : ""}
        >
          {eduMode ? "1" : ""}
        </div>
        <div
          className={hourRow1[1] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleHour1 : ""}
        >
          {eduMode ? "2" : ""}
        </div>
        <div
          className={hourRow1[2] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleHour1 : ""}
        >
          {eduMode ? "3" : ""}
        </div>
        <div
          className={hourRow1[3] ? hourRightOn : hourRightOff}
          title={eduMode ? titleHour1 : ""}
        >
          {eduMode ? "4" : ""}
        </div>
      </div>
      <div className="flex">
        <div
          className={minuteRow5[0] ? minLeftOn : minLeftOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 5 : ""}
        </div>
        <div
          className={minuteRow5[1] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 10 : ""}
        </div>
        <div
          className={minuteRow5[2] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 15 : ""}
        </div>
        <div
          className={minuteRow5[3] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 20 : ""}
        </div>
        <div
          className={minuteRow5[4] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 25 : ""}
        </div>
        <div
          className={minuteRow5[5] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 30 : ""}
        </div>
        <div
          className={minuteRow5[6] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 35 : ""}
        </div>
        <div
          className={minuteRow5[7] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 40 : ""}
        </div>
        <div
          className={minuteRow5[8] ? hourBasicOn : hourBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 45 : ""}
        </div>
        <div
          className={minuteRow5[9] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 50 : ""}
        </div>
        <div
          className={minuteRow5[10] ? minRightOn : minRightOff}
          title={eduMode ? titleMinute5 : ""}
        >
          {eduMode ? 55 : ""}
        </div>
      </div>
      <div className="flex mt-2">
        <div
          className={minuteRow1[0] ? minLeftOn : minLeftOff}
          title={eduMode ? titleMinute1 : ""}
        >
          {eduMode ? 1 : ""}
        </div>
        <div
          className={minuteRow1[1] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute1 : ""}
        >
          {eduMode ? 2 : ""}
        </div>
        <div
          className={minuteRow1[2] ? minBasicOn : minBasicOff}
          title={eduMode ? titleMinute1 : ""}
        >
          {eduMode ? 3 : ""}
        </div>
        <div
          className={minuteRow1[3] ? minRightOn : minRightOff}
          title={eduMode ? titleMinute1 : ""}
        >
          {eduMode ? 4 : ""}
        </div>
      </div>
      {showClock ? <DigitalClock timeElems={timeEls} /> : null}
      <div className="flex justify-center p-5">
        <BasicButton clickHandler={showClockClickHandler}>
          {showClock ? "Hide digital clock" : "Show digital clock"}
        </BasicButton>
        <BasicButton clickHandler={eduModeHandler}>
          {eduMode ? "Turn off educational mode" : "Enable educational mode"}
        </BasicButton>
      </div>
      <ColorThemeSwitcher changeHandler={themeChangeHandler} theme={theme} />
    </div>
  );
};

export { ClockFace };
